import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import {
  asBool,
  asNumber,
  asString,
  cloneDoc,
  ensureArray,
  ensureObject,
  objectMap,
  objectRecord,
  stringList,
  type TenantConfigDoc,
} from '../../../lib/tenant-config-form-utils';
import {
  CheckboxField,
  FieldLabel,
  ItemCard,
  NumberInput,
  RemoveButton,
  SectionBox,
  StringListEditor,
  SubsectionTitle,
  TextInput,
  isValidHexColor,
  normalizeHexColor,
} from './FormPrimitives';
import { Input } from '../../ui/Input';
import { TenantLogo } from '../TenantLogo';
import { TenantLogoUpload } from '../TenantLogoUpload';
import { V2ConfigSliceEditor } from '../config-v2/V2ConfigSliceEditor';

interface TenantConfigFormProps {
  doc: TenantConfigDoc;
  onChange: (doc: TenantConfigDoc) => void;
  tenantCode?: string;
  logoUrl?: string | null;
  onLogoChange?: (logoUrl: string | null) => void;
  masterProducts?: Array<{ id: string; workspaceDir?: string }>;
}

function updateDoc(onChange: (doc: TenantConfigDoc) => void, doc: TenantConfigDoc, mutator: (next: TenantConfigDoc) => void) {
  const next = cloneDoc(doc);
  mutator(next);
  onChange(next);
}

function hasYamlSection(value: unknown): boolean {
  return value != null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length > 0;
}

export function TenantConfigForm({ doc, onChange, tenantCode, logoUrl, onLogoChange }: TenantConfigFormProps) {
  const smtp = useMemo(() => objectRecord(doc.smtp), [doc.smtp]);
  const monitoring = useMemo(() => objectRecord(doc.monitoring), [doc.monitoring]);
  const support = useMemo(() => objectRecord(doc.support), [doc.support]);

  const monitoringServices = useMemo(() => {
    const arr = doc.monitoring && typeof doc.monitoring === 'object' ? (doc.monitoring as Record<string, unknown>).services : [];
    return Array.isArray(arr) ? (arr as Record<string, unknown>[]) : [];
  }, [doc.monitoring]);
  const syntheticSuites = useMemo(() => {
    const syn = objectRecord(monitoring.synthetic);
    return Array.isArray(syn.suites) ? (syn.suites as Record<string, unknown>[]) : [];
  }, [monitoring.synthetic]);
  const environmentMap = useMemo(() => objectMap(monitoring.environmentMap), [monitoring.environmentMap]);
  const alertmanagerEnvs = useMemo(() => {
    const am = objectRecord(monitoring.alertmanager);
    return objectMap(am.environments);
  }, [monitoring.alertmanager]);
  const monitoringJira = objectRecord(monitoring.jira);
  const slaReport = objectRecord(support.slaReport);
  const slaTenant = objectRecord(slaReport.tenant);
  const slaEmail = objectRecord(slaReport.email);
  const slaUpload = objectRecord(slaReport.upload);

  const identity = objectRecord(doc.identity);
  const tenantColorRaw = asString(identity.color);
  const tenantColor = tenantColorRaw.trim() && isValidHexColor(tenantColorRaw)
    ? normalizeHexColor(tenantColorRaw)
    : '#6b7280';
  const tenantDisplayName = asString(identity.displayName) || asString(identity.code) || 'Tenant';
  const tenantCodeLabel = asString(identity.code) || '—';

  const hasSmtpSection = hasYamlSection(doc.smtp);
  const hasMonitoringSection = hasYamlSection(doc.monitoring);
  const hasSupportSection = hasYamlSection(doc.support);

  return (
    <div className="space-y-4 text-sm">
      {/* General */}
      <SectionBox title="General">
        <div
          className="flex items-center gap-3 rounded-lg border-2 px-3 py-2.5 transition-colors"
          style={{
            borderColor: tenantColor,
            backgroundColor: `color-mix(in srgb, ${tenantColor} 12%, transparent)`,
          }}
        >
          <TenantLogo
            tenant={{
              code: tenantCodeLabel,
              name: tenantDisplayName,
              color: tenantColor,
              logoUrl: logoUrl ?? null,
            }}
            size="lg"
            rounded="full"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{tenantDisplayName}</p>
            <p className="truncate font-mono text-xs text-gray-600 dark:text-gray-400">{tenantCodeLabel}</p>
          </div>
        </div>

        {tenantCode ? (
          <FieldLabel label="Tenant logo">
            <TenantLogoUpload
              tenantCode={tenantCode}
              tenantName={tenantDisplayName}
              color={tenantColor}
              logoUrl={logoUrl}
              onChange={onLogoChange}
            />
          </FieldLabel>
        ) : null}

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Identity fields (code, cluster, display name, color, domains) are editable in the <span className="font-medium">YAML v2 — Configuration</span> section below.
        </p>
      </SectionBox>

      <V2ConfigSliceEditor doc={doc} onChange={onChange} scope="tenant" />

      {/* SMTP */}
      {hasSmtpSection ? (
      <SectionBox title="SMTP">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="Host">
            <TextInput
              value={asString(smtp.host)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'smtp').host = v; })}
            />
          </FieldLabel>
          <FieldLabel label="Port">
            <NumberInput
              value={asNumber(smtp.port, 587)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'smtp').port = v; })}
            />
          </FieldLabel>
          <FieldLabel label="User">
            <TextInput
              value={asString(smtp.user)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'smtp').user = v; })}
            />
          </FieldLabel>
          <FieldLabel label="User ref (secret)">
            <TextInput
              value={asString(smtp.user_ref)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'smtp').user_ref = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Password ref (secret)">
            <TextInput
              value={asString(smtp.password_ref)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'smtp').password_ref = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Admin email">
            <TextInput
              value={asString(smtp.admin_email)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'smtp').admin_email = v; })}
            />
          </FieldLabel>
          <CheckboxField
            label="Auth"
            checked={asBool(smtp.auth, true)}
            onChange={(checked) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'smtp').auth = checked; })}
          />
          <CheckboxField
            label="STARTTLS"
            checked={asBool(smtp.starttls, true)}
            onChange={(checked) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'smtp').starttls = checked; })}
          />
        </div>
      </SectionBox>
      ) : null}

      {/* Monitoring */}
      {hasMonitoringSection ? (
      <SectionBox title="Monitoring">
        <CheckboxField
          label="Jira enabled"
          checked={asBool(monitoringJira.enabled)}
          onChange={(checked) =>
            updateDoc(onChange, doc, (n) => {
              ensureObject(ensureObject(n, 'monitoring'), 'jira').enabled = checked;
            })
          }
        />
        <FieldLabel label="Jira project key" className="mt-2">
          <TextInput
            value={asString(monitoringJira.projectKey)}
            onChange={(v) =>
              updateDoc(onChange, doc, (n) => {
                ensureObject(ensureObject(n, 'monitoring'), 'jira').projectKey = v;
              })
            }
            mono
          />
        </FieldLabel>

        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <SubsectionTitle>Environment map</SubsectionTitle>
            <button
              type="button"
              onClick={() => {
                const id = `env-${Object.keys(environmentMap).length + 1}`;
                updateDoc(onChange, doc, (n) => {
                  const m = ensureObject(ensureObject(n, 'monitoring'), 'environmentMap');
                  m[id] = { displayName: '' };
                });
              }}
              className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
          {Object.entries(environmentMap).map(([key, val]) => (
            <div key={key} className="grid grid-cols-[1fr_1fr_auto] gap-1 items-center">
              <Input value={key} disabled className="text-xs font-mono bg-gray-50 dark:bg-gray-800/50" />
              <Input
                value={asString(val.displayName)}
                onChange={(e) =>
                  updateDoc(onChange, doc, (n) => {
                    ensureObject(ensureObject(ensureObject(n, 'monitoring'), 'environmentMap'), key).displayName = e.target.value;
                  })
                }
                placeholder="Display name"
                className="text-xs"
              />
              <RemoveButton
                label="Remove environment map entry"
                onClick={() =>
                  updateDoc(onChange, doc, (n) => {
                    delete ensureObject(ensureObject(n, 'monitoring'), 'environmentMap')[key];
                  })
                }
              />
            </div>
          ))}
        </div>

        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <SubsectionTitle>Services</SubsectionTitle>
            <button
              type="button"
              onClick={() =>
                updateDoc(onChange, doc, (n) => {
                  const m = ensureObject(n, 'monitoring');
                  const arr = ensureArray(m, 'services') as Record<string, unknown>[];
                  arr.push({
                    serviceId: '',
                    environment: '',
                    url: '',
                    downtime_severity: 'alert',
                    displayName: '',
                  });
                })
              }
              className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Plus className="w-3 h-3" /> Add service
            </button>
          </div>
          {monitoringServices.length === 0 && (
            <p className="text-xs text-gray-500">No monitoring services configured.</p>
          )}
          {monitoringServices.map((svc, idx) => (
            <div key={`svc-${idx}`}>
              <ItemCard className="grid grid-cols-1 md:grid-cols-2 gap-2 !p-3">
                <FieldLabel label="Service ID">
                  <TextInput
                    value={asString(svc.serviceId)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(n, 'monitoring'), 'services') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), serviceId: v };
                      })
                    }
                    mono
                  />
                </FieldLabel>
                <FieldLabel label="Environment">
                  <TextInput
                    value={asString(svc.environment)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(n, 'monitoring'), 'services') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), environment: v };
                      })
                    }
                  />
                </FieldLabel>
                <FieldLabel label="Display name">
                  <TextInput
                    value={asString(svc.displayName)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(n, 'monitoring'), 'services') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), displayName: v };
                      })
                    }
                  />
                </FieldLabel>
                <FieldLabel label="Severity">
                  <TextInput
                    value={asString(svc.downtime_severity)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(n, 'monitoring'), 'services') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), downtime_severity: v };
                      })
                    }
                  />
                </FieldLabel>
                <FieldLabel label="URL" className="md:col-span-2">
                  <TextInput
                    value={asString(svc.url)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(n, 'monitoring'), 'services') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), url: v };
                      })
                    }
                    mono
                  />
                </FieldLabel>
                <div className="md:col-span-2 flex justify-end">
                  <RemoveButton
                    label="Remove service"
                    onClick={() =>
                      updateDoc(onChange, doc, (n) => {
                        const m = ensureObject(n, 'monitoring');
                        const arr = [...(ensureArray(m, 'services') as unknown[])];
                        arr.splice(idx, 1);
                        m.services = arr;
                      })
                    }
                  />
                </div>
              </ItemCard>
            </div>
          ))}
        </div>

        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <SubsectionTitle>Synthetic suites</SubsectionTitle>
            <button
              type="button"
              onClick={() =>
                updateDoc(onChange, doc, (n) => {
                  const syn = ensureObject(ensureObject(n, 'monitoring'), 'synthetic');
                  const arr = ensureArray(syn, 'suites') as Record<string, unknown>[];
                  arr.push({ suiteId: '', displayName: '', environment: '', baseUrl: '' });
                })
              }
              className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Plus className="w-3 h-3" /> Add suite
            </button>
          </div>
          {syntheticSuites.map((suite, idx) => (
            <div key={`suite-${idx}`}>
              <ItemCard className="grid grid-cols-1 md:grid-cols-2 gap-2 !p-3">
                <FieldLabel label="Suite ID">
                  <TextInput
                    value={asString(suite.suiteId)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(ensureObject(n, 'monitoring'), 'synthetic'), 'suites') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), suiteId: v };
                      })
                    }
                    mono
                  />
                </FieldLabel>
                <FieldLabel label="Environment">
                  <TextInput
                    value={asString(suite.environment)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(ensureObject(n, 'monitoring'), 'synthetic'), 'suites') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), environment: v };
                      })
                    }
                  />
                </FieldLabel>
                <FieldLabel label="Display name">
                  <TextInput
                    value={asString(suite.displayName)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(ensureObject(n, 'monitoring'), 'synthetic'), 'suites') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), displayName: v };
                      })
                    }
                  />
                </FieldLabel>
                <FieldLabel label="Base URL">
                  <TextInput
                    value={asString(suite.baseUrl)}
                    onChange={(v) =>
                      updateDoc(onChange, doc, (n) => {
                        const arr = ensureArray(ensureObject(ensureObject(n, 'monitoring'), 'synthetic'), 'suites') as Record<string, unknown>[];
                        arr[idx] = { ...objectRecord(arr[idx]), baseUrl: v };
                      })
                    }
                    mono
                  />
                </FieldLabel>
                <div className="md:col-span-2 flex justify-end">
                  <RemoveButton
                    label="Remove suite"
                    onClick={() =>
                      updateDoc(onChange, doc, (n) => {
                        const syn = ensureObject(ensureObject(n, 'monitoring'), 'synthetic');
                        const arr = [...(ensureArray(syn, 'suites') as unknown[])];
                        arr.splice(idx, 1);
                        syn.suites = arr;
                      })
                    }
                  />
                </div>
              </ItemCard>
            </div>
          ))}
        </div>

        <div className="mt-3 space-y-2">
          <SubsectionTitle>Alertmanager environments</SubsectionTitle>
          {Object.entries(alertmanagerEnvs).map(([key, val]) => (
            <div key={key} className="flex items-center gap-2 text-xs">
              <span className="font-mono text-gray-700 dark:text-gray-300 w-24">{key}</span>
              <CheckboxField
                label="Enabled"
                checked={asBool(val.enabled)}
                onChange={(checked) =>
                  updateDoc(onChange, doc, (n) => {
                    ensureObject(ensureObject(ensureObject(n, 'monitoring'), 'alertmanager'), 'environments')[key] = {
                      ...val,
                      enabled: checked,
                    };
                  })
                }
              />
            </div>
          ))}
        </div>
      </SectionBox>
      ) : null}

      {/* Support */}
      {hasSupportSection ? (
      <SectionBox title="Support — SLA report">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="Report type">
            <TextInput
              value={asString(slaReport.reportType)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(ensureObject(n, 'support'), 'slaReport').reportType = v; })}
            />
          </FieldLabel>
          <CheckboxField
            label="Outage analysis"
            checked={asBool(slaReport.outageAnalysis)}
            onChange={(checked) =>
              updateDoc(onChange, doc, (n) => {
                ensureObject(ensureObject(n, 'support'), 'slaReport').outageAnalysis = checked;
              })
            }
          />
          <FieldLabel label="Tenant name">
            <TextInput
              value={asString(slaTenant.name)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(ensureObject(ensureObject(n, 'support'), 'slaReport'), 'tenant').name = v;
                })
              }
            />
          </FieldLabel>
          <FieldLabel label="Jira project name">
            <TextInput
              value={asString(slaTenant.jiraProjectName)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(ensureObject(ensureObject(n, 'support'), 'slaReport'), 'tenant').jiraProjectName = v;
                })
              }
            />
          </FieldLabel>
          <FieldLabel label="Image URL" className="md:col-span-2">
            <TextInput
              value={asString(slaTenant.imageUrl)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(ensureObject(ensureObject(n, 'support'), 'slaReport'), 'tenant').imageUrl = v;
                })
              }
              mono
            />
          </FieldLabel>
        </div>
        <FieldLabel label="Email recipients" className="mt-2">
          <StringListEditor
            items={stringList(slaEmail.to)}
            onChange={(items) =>
              updateDoc(onChange, doc, (n) => {
                ensureObject(ensureObject(ensureObject(n, 'support'), 'slaReport'), 'email').to = items.filter(Boolean);
              })
            }
            placeholder="support@example.com"
          />
        </FieldLabel>
        <div className="flex gap-4 mt-2">
          <CheckboxField
            label="Skip email"
            checked={asBool(slaEmail.skip)}
            onChange={(checked) =>
              updateDoc(onChange, doc, (n) => {
                ensureObject(ensureObject(ensureObject(n, 'support'), 'slaReport'), 'email').skip = checked;
              })
            }
          />
          <CheckboxField
            label="Skip upload"
            checked={asBool(slaUpload.skip)}
            onChange={(checked) =>
              updateDoc(onChange, doc, (n) => {
                ensureObject(ensureObject(ensureObject(n, 'support'), 'slaReport'), 'upload').skip = checked;
              })
            }
          />
        </div>
      </SectionBox>
      ) : null}

      <p className="text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
        Sections appear only when the matching block exists in this tenant&apos;s YAML. Deep nested config (grafana Teams channels, etc.) is preserved when saving from the form and can be edited in the YAML tab.
      </p>
    </div>
  );
}
